import { useEffect, useMemo, useState } from "react";
import { get } from "./api";
import type { UserSummary } from "./api";

/** The value the "nobody covers this" option carries, matching the API. */
export const UNASSIGNED = "none";

/**
 * Admin-only, and only a narrowing: the server intersects the choice with
 * the caller's own scope, so this cannot reveal a customer an unfiltered
 * visit would not have shown. The page hiding it is convenience; the
 * server refusing `?user=` from a CSM is the control.
 *
 * Only active CSMs are offered. Filtering by an admin is a valid request
 * that answers "everything" -- an admin covers every customer and holds no
 * grant rows -- which makes it an option that does nothing.
 */
export function CoverFilter({
  value,
  csms,
  onChange,
}: {
  value: string;
  csms: UserSummary[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="picker">
      <label htmlFor="cover" style={{ marginBottom: 0 }}>
        Covered by
      </label>
      <select id="cover" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Everyone</option>
        <option value={UNASSIGNED}>— Nobody —</option>
        {csms.map((u) => (
          <option key={u.id} value={u.id}>
            {u.name} ({u.email})
          </option>
        ))}
      </select>
      {value && (
        <button className="btn tiny" onClick={() => onChange("")}>
          Clear
        </button>
      )}
    </div>
  );
}

/** The active CSMs, for an admin. Nobody else may ask, and nobody else
    is offered the filter that needs them. */
export function useCsms(isAdmin: boolean): UserSummary[] {
  const [users, setUsers] = useState<UserSummary[]>([]);

  useEffect(() => {
    if (!isAdmin) return;
    void get<{ users: UserSummary[] }>("/api/admin/users")
      .then((d) => setUsers(d.users))
      // A failure here costs the filter, not the page: the customers load
      // from their own request and do not wait on this one.
      .catch(() => setUsers([]));
  }, [isAdmin]);

  return useMemo(() => users.filter((u) => u.is_active && u.role === "csm"), [users]);
}

